import { Color } from '@persona-id/design-tokens';

import BarAndLineChartWithLegendV2 from 'dashboard/components/BarAndLineChartV2';
import { Period } from 'dashboard/lib/unixTime';
import { getEffectiveInterval } from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHour,
  formatChartDateHourChart,
  formatChartDateLong,
  formatChartDateLongWithDay,
  formatChartDateMonth,
  formatChartMonthName,
} from 'dashboard/lib/utils/formatters';
import {
  VerificationVolumeDatum,
  VerificationVolumeSingleInterval,
} from 'dashboard/models/metrics-verification';

const DEFAULT_COLORS = [Color.CoreTeal100, Color.CorePurple700];

const BOLDER_COLORS = [
  Color.CoreTeal900,
  Color.CorePurple800,
  Color.CoreGreen900,
  Color.CoreOrange800,
  Color.CoreYellow900,
  Color.CoreRed900,
  Color.CoreBlue800,
  Color.CoreSlate700,
];

const LIGHTER_COLORS = [
  Color.CoreTeal100,
  Color.CorePurple100,
  Color.CoreGreen100,
  Color.CoreOrange100,
  Color.CoreYellow100,
  Color.CoreRed100,
  Color.CoreBlue100,
  Color.CoreSlate100,
];

// Ordering matters here. This is the order in which the bars are shown.
export interface VerificationsComposedChartProps
  extends Pick<VerificationVolumeSingleInterval, 'period' | 'data'> {
  barKeys: Partial<Record<keyof VerificationVolumeDatum, string>>;
  lineKeys: Partial<Record<string, string>>;
  barsLabel?: string;
  linesLabel?: string;
  grouped: boolean;
  initialFilteredKeys: string[];
  groupedByInquiries: boolean;
  chartPeriod: Period;
  legendIcons: Record<string, JSX.Element>;
}

const VerificationsComposedChart = ({
  data,
  period,
  barKeys,
  barsLabel,
  lineKeys,
  linesLabel,
  grouped,
  initialFilteredKeys,
  groupedByInquiries,
  chartPeriod,
  legendIcons = {},
}: VerificationsComposedChartProps) => {
  const tooltipPeriod = chartPeriod || period;
  const createTooltipHeader = (label: number) => {
    return tooltipPeriod === 'week'
      ? `Week of ${formatChartDateLong(label)}`
      : tooltipPeriod === 'month'
        ? formatChartDateMonth(label)
        : tooltipPeriod === 'day'
          ? formatChartDateLongWithDay(label)
          : formatChartDateHour(label);
  };

  const barAndLineProps = {
    data: data,
    legendFormatter: (value: any) => {
      return barKeys[value as keyof VerificationVolumeDatum] ?? lineKeys[value as string];
    },
    // All reasonable, modern browsers should be preserving the ordering of the keys
    barKeys: Object.keys(barKeys),
    barsLabel: barsLabel,
    lineKeys: Object.keys(lineKeys),
    linesLabel: linesLabel,
    colors: grouped ? BOLDER_COLORS : DEFAULT_COLORS,
    stackColors: LIGHTER_COLORS,
    xDataKey: 'created_timestamp',
    createTooltipHeader: createTooltipHeader,
    xFormatter:
      tooltipPeriod === Period.Hour && period === chartPeriod
        ? formatChartDateHourChart
        : tooltipPeriod === Period.Month
          ? formatChartMonthName
          : formatChartDate,
    stackedBars: grouped,
    initialFilteredLegendKeys: initialFilteredKeys,
    // By Default, show the Observations with 0
    showZeros: true,
    // onDeepLink: onDeepLink}
    xAxisLabel: groupedByInquiries ? 'Inquiry created date' : 'Verification created date',
    // When it is grouped, we will require the scrolling since there will be potential for
    // high cardinality
    scrollEnabled: grouped,
    interval: getEffectiveInterval(period, chartPeriod),
    legendIcons: legendIcons,
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return <BarAndLineChartWithLegendV2 {...barAndLineProps} />;
};

export default VerificationsComposedChart;
